import { useEffect, useRef, useState } from "react";
import type React from "react";
import type { PropsWithChildren, ReactNode } from "react";

import { Button, type ButtonProps } from "#components/v2/Button";
import { SearchField } from "#components/v2/SearchField";
import { Skeleton } from "#components/v2/Skeleton";
import { useUIVersion } from "#hooks";
import { cn, customTv } from "#utils";
import {
  Dialog,
  DialogTrigger,
  type DialogTriggerProps,
  Popover,
  type PressEvent,
} from "react-aria-components";
import { DropdownCTA } from "./DropdownCTA";

import type { PopoverPlacement } from "../shared/MultiDropdownBase";

export interface SingleSelectDropdownProps extends DialogTriggerProps {
  AnchorEle: ReactNode;
  placement?: PopoverPlacement;
  className?: string;
  style?: React.CSSProperties;
  withSearch?: boolean;
  searchValue?: string;
  searchPlaceholder?: string;
  onSearch?: (v: string) => void;
  withCTA?: boolean;
  leftCTA?: {
    action: (e: PressEvent) => void;
    text: string;
  };
  rightCTA?: {
    action: (e: PressEvent) => void;
    text: string;
  };
  /** When true, the popover will match the width of the trigger element */
  matchTriggerWidth?: boolean;
  dialogClassName?: string;
  /** When true, shows loading skeletons in place of the children */
  isPending?: boolean;
  /** Optional heading rendered at the top of the popover */
  label?: string;
  /** Optional description text rendered below the label */
  description?: string;
  /** When true, the trigger element will take full width */
  fullWidth?: boolean;
}

export interface SingleSelectDropdownItemProps
  extends PropsWithChildren, Omit<ButtonProps, "children" | "variant"> {
  isDisabled?: boolean;
  isSelected?: boolean;
}

const singleSelectDropdownItemStyles = customTv({
  base: "w-full text-start",
  variants: {
    uiVersion: {
      v1: "rounded-none text-black hover:bg-stacklet-orange-100 text-body-small font-normal py-2 px-4 hover:bg-background-secondary",
      v2: "px-xs",
    },
    isSelected: {
      true: "bg-interactive-secondary-button-focus-background",
    },
  },
  compoundVariants: [
    {
      uiVersion: "v1",
      isSelected: true,
      className: "bg-background-secondary",
    },
  ],
  defaultVariants: {
    uiVersion: "v2",
  },
});

export function SingleSelectDropdownItem({
  children,
  onPress,
  className,
  isDisabled,
  isSelected,
  slot = "close", // 'close' slot will close the dropdown when clicked - pass null to keep it open
  ...buttonProps
}: SingleSelectDropdownItemProps) {
  const uiVersion = useUIVersion();

  return (
    <li role="menuitem">
      <Button
        {...buttonProps}
        className={singleSelectDropdownItemStyles({
          className,
          uiVersion,
          isSelected,
        })}
        isDisabled={isDisabled}
        onPress={onPress}
        slot={slot}
        variant="dropdown"
      >
        {children}
      </Button>
    </li>
  );
}

const singleSelectDropdownStyles = customTv({
  base: "min-w-[12.5rem] py-xs outline-none rounded-1 flex flex-col gap-y-2",
  variants: {
    uiVersion: {
      v1: "bg-neutral-white px-0 py-4 shadow-lg border border-legacy-navy-L80",
      v2: "border border-outline-decorative bg-background-secondary shadow-md px-1",
    },
  },
  defaultVariants: {
    uiVersion: "v2",
  },
});

export function SingleSelectDropdown({
  AnchorEle,
  children,
  className,
  style,
  placement = "bottom",
  withSearch,
  searchValue,
  searchPlaceholder,
  onSearch,
  withCTA,
  leftCTA,
  rightCTA,
  matchTriggerWidth,
  dialogClassName,
  isPending,
  label,
  description,
  fullWidth = false,
  ...props
}: SingleSelectDropdownProps) {
  const uiVersion = useUIVersion();
  const showCTA = withCTA && leftCTA && rightCTA;
  const showSearch = withSearch && onSearch;

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [triggerWidth, setTriggerWidth] = useState(0);

  useEffect(() => {
    if (!matchTriggerWidth) return;

    const trigger = triggerRef.current;
    if (!trigger) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setTriggerWidth(entry.target.clientWidth);
      }
    });

    observer.observe(trigger);
    return () => {
      observer.unobserve(trigger);
    };
  }, [matchTriggerWidth]);

  return (
    <DialogTrigger {...props}>
      <span
        className={fullWidth ? "flex w-full" : "inline-flex"}
        ref={matchTriggerWidth ? triggerRef : undefined}
      >
        {AnchorEle}
      </span>
      <Popover
        className="scrollbar-custom"
        placement={placement}
        style={matchTriggerWidth ? { minWidth: triggerWidth } : undefined}
      >
        <Dialog
          className={singleSelectDropdownStyles({
            uiVersion,
            className: dialogClassName,
          })}
          style={{ maxHeight: "inherit" }}
        >
          <div className="flex min-h-0 flex-1 flex-col gap-y-2 overflow-y-auto max-h-[min(26rem,70vh)]">
            {label ? (
              <div className="px-4 pb-xs pt-2">
                <p className="text-[0.875rem] font-bold uppercase text-legacy-neutrals-ash">
                  {label}
                </p>
                {description ? (
                  <p className="text-[0.625rem] font-medium text-legacy-navy-L40">{description}</p>
                ) : null}
              </div>
            ) : null}
            {showSearch ? (
              <div className="flex p-0-5">
                <SearchField
                  className={cn("w-full", uiVersion === "v1" ? "mx-4 mb-2 mt-xxs" : "")}
                  onChange={onSearch}
                  placeholder={searchPlaceholder || "Search"}
                  value={searchValue}
                />
              </div>
            ) : null}
            {isPending ? (
              <div className="flex flex-col gap-y-1 p-2">
                <Skeleton width="100%" />
                <Skeleton width="100%" />
                <Skeleton width="100%" />
                <Skeleton width="100%" />
              </div>
            ) : (
              <ol
                className={cn("flex flex-col", className, uiVersion === "v1" ? "" : "gap-y-2")}
                role="menu"
                style={style}
              >
                {children}
              </ol>
            )}
          </div>
          {showCTA ? <DropdownCTA leftCTA={leftCTA} rightCTA={rightCTA} /> : null}
        </Dialog>
      </Popover>
    </DialogTrigger>
  );
}
